import React, { useEffect, useState } from 'react';
import '@/assets/app/css/quiz.css';
import FrameBox from '@/shared/components/ui/new/FrameBox';
import { vAppAjax } from '@/shared/utils/CommonUtil';
import logger from '@/shared/utils/Logger';
import AnswerOImage from '@/assets/app/images/adventure/answer_O.png';
import AnswerXImage from '@/assets/app/images/adventure/answer_X.png';
import loadingQuizImage from '@/assets/app/images/adventure/loading_quiz.png';
import OxBearImage from '@/assets/app/images/common/bottom/ox-bear.png';
import CorrectBearImage from '@/assets/app/images/common/bottom/correct-bear.png';
import IncorrectBearImage from '@/assets/app/images/common/bottom/incorrect-bear.png';
import BookBearImage from '@/assets/app/images/common/bottom/book-bear.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import { useModal } from '@/shared/components/ModalProvider';
import QuizResultDetail from './QuizResult';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import useMissionDialogStore from '@/shared/store/UseMissionDialogStore';
import { SegmentedProgressBar } from '../common/SegmentedProgressBar';

interface QuizProps {
  chapter?: ChapterData;
  question?: string;
  options?: string[];
  correctAnswer?: number; // 정답의 인덱스 (0-3)
  onNext?: () => void;
  onClose?: () => void;
  /** true면 새로 풀지 않고, 완료해둔 결과만 바로 보여준다 (다시 풀기 시 실제 세션 시작) */
  viewResultOnly?: boolean;
}

type QuizState = 'initial' | 'selected' | 'answered';

const Quiz: React.FC<QuizProps> = ({ chapter, onNext, onClose, viewResultOnly = false }) => {
  const [session, setSession] = useState<QuizSession | null>(null);
  const [currentOrder, setCurrentOrder] = useState(1);
  const [quiz, setQuiz] = useState<QuizDetail | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | boolean | null>(null);
  const [answerResult, setAnswerResult] = useState<QuizAnswerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  // 방금 플레이해서 나온 결과가 아니라 이미 클리어한 챕터의 기록을 보여주는 것인지
  const [isPastResult, setIsPastResult] = useState(viewResultOnly);
  const [completedData, setCompletedData] = useState<QuizSessionResult | null>(null);
  const [firstCompletedData, setFirstCompletedData] = useState<QuizSessionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const modal = useModal();
  const { openSheet } = useBottomSheetStore();
  const { openMissionDialog } = useMissionDialogStore();
  const [quizText, setQuizText] = useState('');

  // 인물/시대/전체모험 완료 달성 다이얼로그를 순서대로(낮은 단계 → 높은 단계) 표시
  const showAchievementDialogs = (achievements: QuizCompletionAchievement[]) => {
    if (achievements.length === 0) {
      return;
    }

    const [achievement, ...rest] = achievements;
    // common.css의 .theme-{themeColor} 클래스가 --color-btn을 해당 시대 테마 색상으로 지정해준다
    const themeClass = `theme-${achievement.themeColor}`;
    let title = '';
    let message = '';

    switch (achievement.type) {
      case 'PERSON_COMPLETE':
        title = '정말 멋져요!';
        message = `<span class="${themeClass}" style="color:var(--color-btn);font-weight:700">[${achievement.eraCategoryName}] ${achievement.personName}</span>에 대한<br />모든 모험을 완료했어요`;
        break;
      case 'ERA_COMPLETE':
        title = '훌륭해요!';
        message = `<span class="${themeClass}" style="color:var(--color-btn);font-weight:700">[${achievement.eraCategoryName}]</span> 에 대한<br />모든 모험을 완료했어요`;
        break;
      case 'ADVENTURE_COMPLETE':
        title = '탐험 성공!';
        message = '<span style="color:#F3A9C9;font-weight:700">전체</span> 모험을 완료했어요';
        break;
      default:
        showAchievementDialogs(rest);
        return;
    }

    openMissionDialog({
      title,
      message,
      points: achievement.points ?? undefined,
      onConfirm: () => showAchievementDialogs(rest),
    });
  };

  // 세션 시작 및 퀴즈 로드
  useEffect(() => {
    if (!chapter) {
      return;
    }
    setLoading(true);

    if (viewResultOnly) {
      loadPastResult(chapter);
      return;
    }

    setTimeout(() => {
      setLoading(false);
    }, 2000);
    initializeQuiz(chapter);
  }, []);

  // 현재 순서 변경 시 퀴즈 로드 (결과만 보기 모드에서는 문제를 다시 불러올 필요가 없음)
  useEffect(() => {
    if (session && !viewResultOnly) {
      loadQuiz(currentOrder);
    }
  }, [currentOrder, session]);

  const initializeQuiz = async (chapter: ChapterData, isRetry = false) => {
    try {
      const chapterId = chapter?.chapterId;
      const eraId = chapter?.eraId;
      const eraCategoryId = chapter?.eraCategoryId;
      const personId = chapter?.personId;

      // 세션 시작
      const newSession = await startQuizSession({
        chapterId,
        eraId,
        eraCategoryId,
        personId,
        isRetry,
      });

      // 첫 번째 퀴즈 로드
      await loadQuiz(1);
    } catch (error) {
      console.error('퀴즈 초기화 실패:', error);
      modal.error('퀴즈를 시작하는데 실패했습니다.');
    }
  };

  // 다시하기(재도전) - 별 포인트 소모 없이 바로 새 세션 시작
  const startRetry = () => {
    if (!chapter) return;
    setIsCompleted(false);
    setIsPastResult(false);
    initializeQuiz(chapter, true);
  };

  // 완료해둔 챕터의 결과만 조회 (새 세션을 시작하지 않고 바로 결과 화면으로)
  const loadPastResult = async (chapter: ChapterData) => {
    try {
      await getQuizByOrder(chapter.chapterId, 1);
      await getLastChapterResult(chapter.chapterId);
    } catch (error) {
      console.error('결과 조회 실패:', error);
      modal.error('결과를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadQuiz = async (order: number) => {
    try {
      setCompletedData(null);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setSubmitting(false);
      setCurrentOrder(order);

      if (!chapter) {
        return;
      }

      await getQuizByOrder(chapter.chapterId, order);
    } catch (error) {
      console.error('퀴즈 로딩 실패:', error);
      modal.error('퀴즈를 불러오는데 실패했습니다.');
    }
  };

  const handleMultipleChoiceSelect = (choiceNumber: number) => {
    if (answerResult) return; // 이미 제출한 경우
    setSelectedAnswer(choiceNumber);
  };

  const handleOXSelect = (answer: boolean) => {
    if (answerResult) return; // 이미 제출한 경우
    setSelectedAnswer(answer);
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null || !session || !quiz) {
      modal.warn('답을 선택해주세요.');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = {
        sessionId: session.sessionId,
        questionId: quiz.questionId,
        memberChoice: typeof selectedAnswer === 'number' ? selectedAnswer : undefined,
        memberOxAnswer: typeof selectedAnswer === 'boolean' ? selectedAnswer : undefined,
      };

      const result = await submitQuizAnswer(submitData);
    } catch (error: any) {
      console.error('답안 제출 실패:', error);
      modal.error(error.response?.data?.message || '답안 제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!quiz) return;

    if (currentOrder < quiz.totalQuestions) {
      setCurrentOrder(currentOrder + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!session) return;

    try {
      await completeQuizSession(session.sessionId);
    } catch (error: any) {
      console.error('퀴즈 완료 처리 실패:', error);
      modal.error(error.response?.data?.message || '퀴즈 완료 처리 중 오류가 발생했습니다.');
    }
  };

  const onBack = () => {
    openSheet('exit-quiz', { sessionId: session?.sessionId, onCancel: onClose });
  };

  /**
   * 챕터별 전체 퀴즈 목록 조회
   * @param params
   */
  const getQuizList = async () => {
    await vAppAjax(
      'get',
      'quiz/list/${chapterId}',
      {},
      (response: AxiosResponse<CommonResult<QuizSession>>) => {
        const resultData = response.data;
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 특정 순서의 퀴즈 조회
   * @param params
   */
  const getQuizByOrder = async (chapterId: number, order: number) => {
    await vAppAjax(
      'post',
      `quiz/${chapterId}/${order}`,
      {},
      (response: AxiosResponse<CommonResult<QuizDetail>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setQuiz(resultData.result);
          setQuizText(resultData.result.questionText);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 퀴즈 세션 시작
   * @param params
   */
  const startQuizSession = async (params: QuizSessionStartRequest) => {
    await vAppAjax(
      'post',
      'quiz/session/start',
      params,
      (response: AxiosResponse<CommonResult<QuizSession>>) => {
        const resultData = response.data;

        if (resultData.code === 200) {
          resultData.result.isRetry = params.isRetry;
          setSession(resultData.result);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 답안 제출
   * @param params
   */
  const submitQuizAnswer = async (params: QuizAnswerSubmit) => {
    await vAppAjax(
      'post',
      'quiz/session/answer',
      params,
      (response: AxiosResponse<CommonResult<QuizAnswerResponse>>) => {
        const resultData = response.data;

        if (resultData.code === 200) {
          const quizAnswer = resultData.result;
          setAnswerResult(quizAnswer);

          // 정답 틀렸을 때만
          if (!quizAnswer.isCorrect) {
            if (quiz?.questionType === 'MULTIPLE_CHOICE') {
              if (quiz.explanation) {
                setQuizText(quiz.explanation);
                return;
              }
              if (!quizAnswer.correctAnswer) {
                return;
              }
              const correct: 'choice1' | 'choice2' | 'choice3' | 'choice4' =
                `choice${quizAnswer.correctAnswer as 1 | 2 | 3 | 4}`;
              const correctText = quiz[correct];
              if (correctText) {
                setQuizText(correctText);
              }
            } else {
              setQuizText(quizAnswer.oxAnswer);
            }
          }
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 퀴즈 세션 완료
   * @param params
   */
  const completeQuizSession = async (sessionId: number) => {
    await vAppAjax(
      'post',
      `quiz/session/complete/${sessionId}`,
      {},
      (response: AxiosResponse<CommonResult<QuizSessionResult>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setIsCompleted(true);
          setCompletedData(resultData.result);
          setFirstCompletedData(prev => prev ?? resultData.result);

          if (resultData.result.achievements?.length) {
            showAchievementDialogs(resultData.result.achievements);
          }
        } else {
          console.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 완료해둔 챕터의 가장 최근 결과 조회 (다시 풀지 않고 결과만 보기)
   * @param chapterId
   */
  const getLastChapterResult = async (chapterId: number) => {
    await vAppAjax(
      'get',
      `quiz/session/last-result/${chapterId}`,
      {},
      (response: AxiosResponse<CommonResult<QuizSessionResult>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setCompletedData(resultData.result);
          setFirstCompletedData(resultData.result);
          setSession({
            sessionId: resultData.result.sessionId,
            memberId: 0,
            chapterId,
            startedAt: '',
            isCompleted: true,
            totalQuestions: resultData.result.totalQuestions,
            correctAnswers: resultData.result.correctAnswers,
            earnedPoints: resultData.result.earnedPoints,
            eraId: chapter?.eraId ?? 0,
            eraCategoryId: chapter?.eraCategoryId ?? 0,
            personId: chapter?.personId ?? 0,
            isRetry: false,
          });
          setIsCompleted(true);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 세션 진행 상황 조회
   * @param params
   */
  const getSessionProgress = async (sessionId: number) => {
    await vAppAjax(
      'get',
      `quiz/session/progress/${sessionId}`,
      {},
      (response: AxiosResponse<CommonResult<QuizSessionResult>>) => {
        const resultData = response.data;
        // if (resultData.code === 200) {
        //   setEraCategoryList(resultData.result);
        // } else {
        //   console.warn(resultData.message);
        // }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  if (loading || !quiz || !session) {
    // 결과만 보기 모드에서는 "곧 퀴즈가 시작됩니다" 스플래시를 보여줄 필요가 없음
    if (viewResultOnly) {
      return <div className="w-full h-full" />;
    }
    return <img src={loadingQuizImage} className="w-full h-full" />;
  }

  if (isCompleted && completedData) {
    return (
      <QuizResultDetail
        completedData={completedData}
        firstCompletedData={firstCompletedData ?? completedData}
        chapter={chapter}
        session={session}
        isPastResult={isPastResult}
        retryQuiz={startRetry}
      />
    );
  }

  return (
    <>
      <div className="quiz-container" style={{ borderTop: '5px solid var(--color-btn)' }}>
        <header className="grid grid-cols-[1fr_auto_1fr] items-center w-full mb-[15px] px-4 py-2 bg-white">
          {chapter && chapter.questionCount > currentOrder ? (
            <div className="flex justify-start">
              <button className="btn-back-theme f-one-pop" onClick={onBack}>
                모험종료
              </button>
            </div>
          ) : (
            <>
              <div></div>
            </>
          )}
          {/* 1. 왼쪽: 뒤로가기 버튼 영역 (왼쪽 정렬) */}

          {/* 2. 가운데: 타이틀 영역 (양쪽 버튼 크기와 상관없이 항상 정중앙 고정) */}
          <div className="text-center quiz-title">{chapter?.personName}</div>

          {/* 3. 오른쪽: 챕터 표시 영역 (오른쪽 정렬) */}
          <div className="flex justify-end">
            <span
              className="text-[14px] font-medium font-bold"
              style={{ color: 'var(--color-btn-border)' }}
            >
              {chapter?.chapterTitle}
            </span>
          </div>
        </header>
        <div className="px-4">
          <div className="page-indicators">
            {[...Array(chapter?.questionCount)].map((_, index) => (
              <div
                key={index}
                className="indicator w-[10px] h-[10px]"
                style={{
                  backgroundColor: `${index <= currentOrder - 1 ? 'var(--color-btn)' : ''}`,
                }}
              />
            ))}
          </div>
          <FrameBox
            style={{
              margin: '15px 0 30px 0',
              backgroundColor: 'white',
              fontSize: 20,
              minHeight: 120,
              padding: '15px 30px',
            }}
          >
            <div className="text-[#C8C8C8] text-[12px] mb-1">
              문제 {currentOrder} {answerResult && <>문제풀이</>}
            </div>
            <div
              className={`font-bold text-[16px] ${!answerResult || answerResult?.isCorrect ? '' : 'text-[#FF6969]'}`}
            >
              {quizText}
            </div>
          </FrameBox>
          {/* 4지선다형 */}
          {quiz.questionType === 'MULTIPLE_CHOICE' && (
            <div className="quiz-choices multiple-choice">
              {[
                { num: 1, text: quiz.choice1 },
                { num: 2, text: quiz.choice2 },
                { num: 3, text: quiz.choice3 },
                { num: 4, text: quiz.choice4 },
              ].map(choice => (
                <button
                  key={choice.num}
                  className={`choice-button ${selectedAnswer === choice.num ? 'selected' : ''} ${
                    answerResult && answerResult.correctAnswer === choice.num ? 'correct' : ''
                  } ${
                    answerResult && selectedAnswer === choice.num && !answerResult.isCorrect
                      ? 'wrong'
                      : ''
                  }`}
                  onClick={() => handleMultipleChoiceSelect(choice.num)}
                  disabled={answerResult !== null}
                >
                  <div className="choice-content">
                    <span className="choice-label">{choice.text}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* O/X형 */}
          {quiz.questionType === 'OX' && (
            <div className="quiz-choices ox-choice">
              <button
                className={`ox-button o-button ${selectedAnswer === true ? 'selected' : ''} ${
                  answerResult && answerResult.correctOxAnswer === true ? 'correct' : ''
                } ${answerResult && selectedAnswer === true && !answerResult.isCorrect ? 'wrong' : ''}`}
                onClick={() => handleOXSelect(true)}
                disabled={answerResult !== null}
              >
                <div className="icon-css-o"></div>
              </button>
              <button
                className={`ox-button x-button ${selectedAnswer === false ? 'selected' : ''} ${
                  answerResult && answerResult.correctOxAnswer === false ? 'correct' : ''
                } ${
                  answerResult && selectedAnswer === false && !answerResult.isCorrect ? 'wrong' : ''
                }`}
                onClick={() => handleOXSelect(false)}
                disabled={answerResult !== null}
              >
                <div className="icon-css-x"></div>
              </button>
            </div>
          )}
          {/* 해설 */}
          {/* {answerResult && answerResult.explanation && (
        <div className="quiz-explanation">
          <h3>💡 해설</h3>
          <p>{answerResult.explanation}</p>
        </div>
      )} */}
        </div>
        <div className="bottom-btn-group">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 15,
              marginBottom: 5,
            }}
          >
            <button
              className="w-[155px] h-[40px] bg-white text-[16px] font-bold"
              style={{
                borderRadius: 40,
                color: `${!answerResult ? '#C8C8C8' : answerResult.isCorrect ? '#0070D6' : '#FF6969'}`,
              }}
              disabled={true}
            >
              {answerResult
                ? answerResult.isCorrect
                  ? '야호! 정답이야'
                  : '아쉬워라, 오답이에요'
                : '신중하게 생각해보자'}
            </button>
            <img
              src={
                answerResult
                  ? answerResult.isCorrect
                    ? CorrectBearImage
                    : IncorrectBearImage
                  : quiz.questionType === 'MULTIPLE_CHOICE'
                    ? BookBearImage
                    : OxBearImage
              }
              style={{ maxWidth: 'max-content', height: 120 }}
            />
          </div>
          <AppButton
            onConfirm={!answerResult ? handleSubmit : handleNext}
            className="f-one-pop text-[20px]"
            backgroundColor={answerResult && !answerResult?.isCorrect ? '#FF6969' : undefined}
            boxShadowColor={answerResult && !answerResult?.isCorrect ? '#EC0000' : undefined}
            message={
              !answerResult ? '정답 확인' : currentOrder >= quiz.totalQuestions ? '확인' : '다음 문제'
            }
          />
        </div>
      </div>
    </>
  );
};

export default Quiz;
